import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'wouter';
import { useCallbackUrl, useCheckEmailMutation, usePasswordSignInMutation, useResetPasswordMutation, useSendOtpMutation, useSendResetOtpMutation, useVerifyOtpMutation } from '../hooks/mutations';
import { Colors } from '../patterns/Colors';
import { EmailEntryForm } from './EmailEntryForm';
import { OtpCodeForm } from './OtpCodeForm';
import { PasswordLoginForm } from './PasswordLoginForm';
import { ResetPasswordForm } from './ResetPasswordForm';

type Step = 'default' | 'otp-sent' | 'password' | 'reset-password' | 'sso';

interface Props {
    children?: React.ReactNode;
}

export const EmailLoginView = ({ children }: Props) => {

    const [, setLocation] = useLocation();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [step, setStep] = useState<Step>('default');
    const [ssoUrl, setSsoUrl] = useState<string | null>(null);

    const checkMutation = useCheckEmailMutation();
    const sendMutation = useSendOtpMutation();
    const verifyMutation = useVerifyOtpMutation();
    const passwordMutation = usePasswordSignInMutation();
    const sendResetMutation = useSendResetOtpMutation();
    const resetMutation = useResetPasswordMutation();

    const errorMessage = checkMutation.error?.message ?? sendMutation.error?.message ?? null;
    const callbackURL = useCallbackUrl();

    useEffect(() => {
        if (step === 'sso' && ssoUrl) {
            window.location.href = ssoUrl;
        }
    }, [step, ssoUrl]);

    const onLookup = async () => {
        const r = await checkMutation.mutateAsync({ email, source: callbackURL });
        if (r.status === 'register') {
            // Carry through every existing query param (boardId, returnUrl, etc.) so the
            // register flow computes the same callback URL; email + source are layered on top.
            const params = new URLSearchParams(window.location.search);
            params.set('email', email);
            params.set('source', callbackURL);
            return setLocation(`/login/register?${params.toString()}`);
        }
        if (r.status === 'sso') {
            setSsoUrl(r.loginUrl ?? null);
            return setStep('sso');
        }
        if (r.status === 'password') {
            return setStep('password');
        }
        await sendMutation.mutateAsync({ email });
        setStep('otp-sent');
    };

    const onVerify = async () => {
        await verifyMutation.mutateAsync({ email, otp: code });
    };

    const onResend = async () => {
        setCode('');
        verifyMutation.reset();
        await sendMutation.mutateAsync({ email });
    };

    const onPasswordSignIn = async () => {
        await passwordMutation.mutateAsync({ email, password });
    };

    // Escape hatch from the password step — passwords are opt-in, so the OTP route always
    // stays available.
    const onUseCodeInstead = async () => {
        passwordMutation.reset();
        await sendMutation.mutateAsync({ email });
        setStep('otp-sent');
    };

    const onForgotPassword = async () => {
        passwordMutation.reset();
        setCode('');
        setPassword('');
        await sendResetMutation.mutateAsync({ email });
        setStep('reset-password');
    };

    const onResetPassword = async () => {
        await resetMutation.mutateAsync({ email, otp: code, password });
    };

    const onResendReset = async () => {
        setCode('');
        resetMutation.reset();
        await sendResetMutation.mutateAsync({ email });
    };

    if (step === 'sso') {
        return <SsoRedirecting>Redirecting to your sign-in provider…</SsoRedirecting>;
    }

    if (step === 'password') {
        return <PasswordLoginForm
            email={email}
            password={password}
            onPasswordChange={setPassword}
            onSubmit={() => void onPasswordSignIn()}
            onUseCode={() => void onUseCodeInstead()}
            onForgotPassword={() => void onForgotPassword()}
            signingIn={passwordMutation.isPending}
            sendingCode={sendMutation.isPending || sendResetMutation.isPending}
            error={passwordMutation.error?.message ?? sendMutation.error?.message ?? sendResetMutation.error?.message ?? null}
        />;
    }

    if (step === 'reset-password') {
        return <ResetPasswordForm
            email={email}
            code={code}
            password={password}
            onCodeChange={setCode}
            onPasswordChange={setPassword}
            onSubmit={() => void onResetPassword()}
            onResend={() => void onResendReset()}
            resetting={resetMutation.isPending}
            resending={sendResetMutation.isPending}
            error={resetMutation.error?.message ?? sendResetMutation.error?.message ?? null}
        />;
    }

    if (step === 'otp-sent') {
        return <OtpCodeForm
            email={email}
            code={code}
            onCodeChange={setCode}
            onSubmit={() => void onVerify()}
            onResend={() => void onResend()}
            verifying={verifyMutation.isPending}
            resending={sendMutation.isPending}
            error={verifyMutation.error?.message ?? sendMutation.error?.message ?? null}
        />;
    }

    return <>
        <EmailEntryForm
            email={email}
            onEmailChange={setEmail}
            onSubmit={() => void onLookup()}
            loading={checkMutation.isPending || sendMutation.isPending}
        />
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
        {children}
    </>;
};

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0.5rem 0 0;
`;

const SsoRedirecting = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    text-align: center;
    margin: 1rem 0;
`;
